import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useMessage } from '../../contexts/MessageContext';
import { useNavigate, useParams } from 'react-router-dom';

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const ExpenseForm = ({ onSuccess }) => {
  const [form, setForm] = useState({
    date: '',
    category: '',
    amount: '',
    description: '',
    paymentmethod:''
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const { logout, getToken } = useAuth();
  const { showSuccess, showError } = useMessage();
  const navigate = useNavigate();
  const { uuid } = useParams();

 const paymentMethods = [
    {value: "cash", Label:"Cash"},
    {value: "card", Label:"Card"},
    {value: "bank", Label:"Bank"}
]

  const isEdit = uuid && uuid.length > 20; // quick way to detect a UUID in path
  const token = getToken ? getToken() : localStorage.getItem('token');

  // Handle form changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Fetch accounts for dropdown
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${apiUrl}/account`, {
        headers: { 'x-auth-token': token }
      });
      if (res.data.success) {
        setCategories(res.data.data || []);
      } else {
        showError(res.data.message || 'Failed to fetch accounts');
      }
    } catch (err) {
      console.error(err);
      showError('Failed to fetch accounts');
    }
  };

  // Fetch existing expense for editing
  const fetchExpense = async () => {
    try {
      const res = await axios.get(`${apiUrl}/expense/${uuid}`, {
        headers: { 'x-auth-token': token }
      });
      if (res.data.success && res.data.data) {
        const expense = res.data.data;
        setForm({
          date: expense.date || '',
          category: expense.category || expense.account?.uuid || '',
          amount: expense.amount || '',
          description: expense.description || '',
          paymentmethod: expense.paymentmethod || ''
        });
      } else {
        showError(res.data.message || 'Failed to fetch expense');
      }
    } catch (err) {
      console.error(err);
      showError('Failed to fetch expense');
    }
  };

  // Load data when component mounts
  useEffect(() => {
    if (!token) {
      logout();
      showError('Authentication required. Please log in.');
      return;
    }

    fetchCategories();
    if (isEdit) fetchExpense();
  }, []);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!token) {
        logout();
        showError('Authentication required. Please log in.');
        return;
      }

      const endpoint = isEdit
        ? `${apiUrl}/expense/${uuid}`
        : `${apiUrl}/expense`;

      const method = isEdit ? 'put' : 'post';

      const res = await axios({
        method,
        url: endpoint,
        headers: { 'x-auth-token': token },
        data: form
      });

      if (res.data.success) {
        showSuccess(`Expense ${isEdit ? 'updated' : 'saved'} successfully!`);
        //redirect to expense list after edit
        if (isEdit) {
          setTimeout(() => {window.location.href = '/expense';}, 1200);
        }
        if (!isEdit) {
          setForm({ date: '', category: '', amount: '', description: '' });
        }
        if (onSuccess) onSuccess();
      } else {
        showError(res.data.message || 'Failed to save expense');
      }
    } catch (err) {
      console.error(err);
      showError('Failed to save expense');
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-lg mx-auto bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold text-gray-800 mb-4">
        {isEdit ? 'Edit Expense' : 'Add Expense'}
      </h2>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
        <input
          type="date"
          name="date"
          value={form.date}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Category (Account)</label>
        <select
          name="category"
          value={form.category || ""}
          onChange={handleChange}
          className="w-full border px-3 py-2 rounded"
          required
        >
          <option value="">Select category</option>
          {categories.map((cat) => (
            <option key={cat.uuid} value={cat.uuid}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>
     <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
        <select
          name="paymentmethod"
          value={form.paymentmethod || ""}
          onChange={handleChange}
          className="w-full border px-3 py-2 rounded"
          required
        >
          <option value="">Select method</option>
          {paymentMethods.map((val) => (
          <option key={val.value} value={val.value}>
            {val.Label}
          </option>
          ))}
        </select>
      </div>


      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
        <input
          type="number"
          name="amount"
          value={form.amount}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          required
          min="0"
          step="0.01"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
        />
      </div>

      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? (isEdit ? 'Updating...' : 'Saving...') : (isEdit ? 'Update Expense' : 'Save Expense')}
        </button>
          <button
            type="button"
            className="ml-4 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            onClick={() => navigate('/expense')}
          >
            Cancel
          </button>
      </div>
    </form>
  );
};

export default ExpenseForm;